import React from 'react';

import { PillBadge } from '@lightspeed/flame/Badge';
import { Bone } from '@lightspeed/flame/Bone';
import { Box, Flex } from '@lightspeed/flame/Core';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { IconHelp } from '@lightspeed/flame/Icon/Help';
import { Text } from '@lightspeed/flame/Text';
import { Tooltip } from '@lightspeed/flame/Tooltip';

import { getLanguage } from 'helpers/locale';
import { createReactRenderer } from 'helpers/react-renderer';
import { NetworkTestEntity } from 'root/apps/NetworkHealthTool/services/NetworkHealthToolService';
import { useNetworkTestService } from 'root/apps/NetworkHealthTool/services/useNetworkHealthToolService';
import {
  ChannelMessage,
  createBroadcastChannel,
  NetworkEventType,
} from 'root/apps/NetworkHealthTool/utils/channelUtils';
import {
  computeNextRunFromElapsed,
  getBackoff,
} from 'root/apps/NetworkHealthTool/utils/timingUtils';
import { logError } from 'root/datadog/datadogLogs';

const formatNumber = (n?: number, digits = 1) =>
  Number.isFinite(n as number) ? (n as number).toFixed(digits) : '–';

export const NetworkStatus: React.FC = () => {
  const service = useNetworkTestService();
  const [last, setLast] = React.useState<NetworkTestEntity | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [channel] = React.useState(createBroadcastChannel());
  const [backoffTime, setBackoffTime] = React.useState(0);

  React.useEffect(() => {
    if (!service) return;
    if (!loading) return;
    setLoading(false);
    service
      .getLastNetworkTest()
      .then((entity) => {
        if (entity) setLast(entity);
      })
      .catch((error) => {
        logError(error, {
          message: 'NetworkStatus failed to fetch last network test',
          source: 'NetworkHealthTool',
          file: 'NetworkStatus.tsx',
        });
      });
    if (getBackoff()) {
      service
        .getTimeSinceLastTest()
        .then((elapsed) => {
          setBackoffTime(computeNextRunFromElapsed(elapsed));
        })
        .catch((error) => {
          logError(error, {
            message: 'NetworkStatus failed to fetch time since last test',
            source: 'NetworkHealthTool',
            file: 'NetworkStatus.tsx',
          });
        });
    }
  }, [loading, service]);

  React.useEffect(() => {
    const handler = (event: MessageEvent<ChannelMessage>) => {
      if (event.data?.type === NetworkEventType.NETWORK_TEST_ADDED) {
        setLoading(false);
        setLast(event.data.payload as NetworkTestEntity);
        setBackoffTime(0);
      }
      if (event.data?.type === NetworkEventType.NETWORK_TEST_RUN_FAILED) {
        service.getTimeSinceLastTest().then((elapsed) => {
          setBackoffTime(computeNextRunFromElapsed(elapsed));
        });
      }
    };
    channel.addEventListener('message', handler);
    return () => {
      channel.removeEventListener('message', handler);
      channel.close();
    };
  }, [channel, service, setLast]);

  const formattedBackoffTime = React.useMemo(() => {
    const rtf = new Intl.RelativeTimeFormat(getLanguage(), { numeric: 'auto' });
    return rtf.format(Math.ceil(backoffTime / 60 / 1000), 'minutes');
  }, [backoffTime]);

  const tooltipContent =
    'Monitors your network health and connectivity to ensure your devices are connected and performing as expected.';

  const rtcName = backoffTime > 0 ? 'unknown' : last?.scores?.rtc?.classificationName ?? 'unknown';
  const latency = formatNumber(last?.latency, 2) ?? '';
  const lastChecked = last ? new Date(last.timestamp).toLocaleString() : 'no data yet';

  const rtcStatus = (status: string) => {
    switch (status) {
      case 'bad':
        return theme.colors.danger;
      case 'poor':
        return theme.colors['maple-200'];
      case 'average':
        return theme.colors.warning;
      case 'good':
        return theme.colors['green-200'];
      case 'great':
        return theme.colors['green-600'];
      default:
        return theme.colors['gray-100'];
    }
  };

  const textColor = ['bad', 'great'].includes(rtcName) ? theme.colors.white : theme.colors.black;

  return (
    <Box>
      <Flex justifyContent="space-between">
        <Flex>
          <Text fontWeight={600} pr={1}>
            Your Network Status
          </Text>
          <Tooltip content={tooltipContent}>
            <span aria-label="Network status help">
              <IconHelp />
            </span>
          </Tooltip>
        </Flex>
        <PillBadge color={textColor} bg={rtcStatus(rtcName)} data-test="network-status-text">
          <Text letterSpacing="0px">{loading ? <Bone /> : rtcName}</Text>
        </PillBadge>
      </Flex>
      <Box mt={2}>
        {backoffTime > 0 ? (
          <>
            <Text size="small" data-test="network-status-failed">
              <strong>Network check failed</strong>
            </Text>
            <Text size="small" marginY={1} data-test="network-status-next-test">
              Next check scheduled{' '}
              <var>
                <strong>{formattedBackoffTime}</strong>
              </var>
            </Text>
          </>
        ) : (
          <>
            <Text size="small" data-test="network-status-latency">
              Latency:{' '}
              {loading ? (
                <Bone />
              ) : (
                <var>
                  <strong>{latency} ms</strong>
                </var>
              )}
            </Text>
            <Text size="small" marginY={1} data-test="network-status-last-checked">
              Last checked:{' '}
              {loading ? (
                <Bone />
              ) : (
                <var>
                  <strong>{lastChecked}</strong>
                </var>
              )}
            </Text>
          </>
        )}
        {/* Waiting on url for full speed test page */}
        {/* <TextLink size="small">
          View network details <IconExternalLink color="blue" />
        </TextLink> */}
      </Box>
    </Box>
  );
};

const RenderedComponent = createReactRenderer({
  component: NetworkStatus,
  selector: '[data-react-root="NetworkStatus"]',
});

export const mountNetworkStatus = () => {
  const host = document.querySelector('[data-react-root="NetworkStatus"]');
  if (host) RenderedComponent.mount();
};

export const unmountNetworkStatus = () => {
  RenderedComponent.unmount();
};
